import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatError } from '@angular/material/input';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Observable, Subject, merge, of } from 'rxjs';
import { catchError, first, map, shareReplay, startWith, switchMap, withLatestFrom } from 'rxjs/operators';

import { BasePageComponent } from '@app/pages/base-page/base-page.component';
import { PasswordVisibilityComponent } from '@shared/form-controls';
import { AuthService } from '@shared/services';
import { AdcButtonComponent } from '@shared/ui-kit/adc-button/adc-button.component';
import { AdcInputComponent } from '@shared/ui-kit/adc-input/adc-input.component';

import { ErrorResponse } from './set-password-page.types';
import { passwordMatchValidator, passwordStrongValidator } from '../../validators';

@Component({
    selector: 'set-password-page',
    templateUrl: './set-password-page.component.html',
    styleUrls: ['../../pages.scss', './set-password-page.scss'],
    imports: [
        MatProgressSpinner,
        BasePageComponent,
        ReactiveFormsModule,
        PasswordVisibilityComponent,
        MatError,
        MatProgressBar,
        AsyncPipe,
        AdcButtonComponent,
        AdcInputComponent,
        RouterLink,
    ],
})
export class SetPasswordPageComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private formBuilder = inject(UntypedFormBuilder);
    private authService = inject(AuthService);

    get passwordErrorMessages(): Record<string, string> {
        return {
            required: 'Password is required',
            strong: 'Minimum 8 characters, at least 1 lower-case letter, upper-case letter and number',
        };
    }

    get confirmPasswordErrorMessages(): Record<string, string> {
        return {
            required: 'Confirm password is required',
            match: 'New password and confirmed password do not match',
        };
    }

    form = this.formBuilder.group({
        password: [null, [Validators.required, passwordStrongValidator]],
        confirmPassword: [null, [Validators.required, passwordMatchValidator]],
    });

    token$ = this.route.params.pipe(
        first(),
        map(({ token }) => token),
    );

    isTokenValid$ = this.token$.pipe(
        switchMap((token) => (token ? this.checkIfTokenValid(token) : of(false))),
        shareReplay(1),
    );
    checkingTokenValidity$ = this.isTokenValid$.pipe(
        map(() => false),
        startWith(true),
    );

    private submitForm$ = new Subject<void>();

    private sendNewPassword$ = this.submitForm$.pipe(
        withLatestFrom(this.token$),
        switchMap(([, token]) => this.authService.setNewPassword(token, this.form.value.password)),
        shareReplay(1),
    );

    submissionProcessing$ = merge(
        this.submitForm$.pipe(map(() => true)),
        this.sendNewPassword$.pipe(
            map(() => false),
            catchError(() => of(false)),
        ),
    );

    passwordErrorMessage$ = this.sendNewPassword$.pipe(
        map((): string | null => null),
        catchError((error) => this.getErrorMessage(error)),
    );

    private readonly destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        this.form.controls.password.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.form.controls.confirmPassword.updateValueAndValidity());

        this.sendNewPassword$
            .pipe(
                switchMap((authToken) => this.authService.loginByAuthToken(authToken)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    onSubmit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.submitForm$.next();
    }

    onGotoLogin(): void {
        this.router.navigate(['login'], { queryParams: { force: true } });
    }

    private checkIfTokenValid(token: string): Observable<boolean> {
        return this.authService.checkTokenForValidity(token).pipe(
            map(() => true),
            catchError(() => of(false)),
        );
    }

    private getErrorMessage(error: ErrorResponse): string {
        // TODO: clarify response error shape
        const defaultMessage = 'We encountered a problem, please try again later';
        const message: string = error.error?.message || error.message || defaultMessage;
        const tokenErrorType: string = error.token || error.error.token;

        if (tokenErrorType !== 'error.validation') return message;

        const fieldErrors = error.fieldErrors || error.error?.fieldErrors;

        if (!fieldErrors) return message;

        const targetError = fieldErrors.find(({ fieldKey }) => fieldKey === 'password');

        if (!targetError) return message;

        return targetError?.messages[0] || message;
    }
}
